import type { I18n } from "@lingui/core";
import { msg } from "@lingui/macro";
import type { IntakeData } from "@/lib/types";
import type { CardDef } from "./types";

/** Order for flattening dental follow-up cards when multiple procedure types are selected. */
export const DENTISTRY_PROCEDURE_TYPE_ORDER = [
  "implants",
  "veneers",
  "crowns",
  "whitening",
  "orthodontics",
  "restoration",
  "cosmetic",
  "other",
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getProcedureSlug = (p: IntakeData["procedures"]): string => {
  if (typeof p === "string") return p;
  return p?.slug || "";
};

const normalizeProcedureSlug = (slug: string): string =>
  slug.trim().toLowerCase().replace(/_/g, "-");

// ─── Main builder function ────────────────────────────────────────────────────
// All strings go through i18n._() so they're extracted by lingui and translated.
export function buildCardFlow(
  intakeData: IntakeData,
  _trustedSessionStatus: "idle" | "loading" | "ready" | "error",
  dentalDynamicCards: CardDef[] = [],
  i18n?: I18n,
): CardDef[] {
  // Helper: translate or fallback to original if no i18n provided
  const t = (md: ReturnType<typeof msg>) => i18n ? i18n._(md) : (md as unknown as string);

  const slug = normalizeProcedureSlug(getProcedureSlug(intakeData.procedures));

  // ─── Personal cards ─────────────────────────────────────────────────────────
  const PERSONAL_CARDS: CardDef[] = [
    {
      id: "preferredLanguage",
      cardType: "language-select",
      question: t(msg`What is your preferred language?`),
      fieldKey: "preferredLanguage",
      options: [
        { value: "en", label: t(msg`English`), emoji: "🇺🇸" },
        { value: "es", label: t(msg`Español`), emoji: "🇪🇸" },
      ],
      autoAdvance: true,
    },
    {
      id: "fullName",
      cardType: "name-input",
      question: t(msg`What's your name?`),
    },
    {
      id: "contactInfo",
      cardType: "contact-info",
      question: t(msg`What's your contact info?`),
      hint: t(msg`We'll send your consultation details here.`),
    },
  ];

  // ─── Speciality selection card ─────────────────────────────────────────────
  const PROCEDURE_CARD: CardDef = {
    id: "procedures",
    cardType: "procedure-select",
    question: t(msg`Which speciality are you interested in?`),
    hint: t(msg`Select the one that best describes your medical need.`),
    fieldKey: "procedures",
    autoAdvance: true,
  };

  // ─── Dentistry base cards ──────────────────────────────────────────────────
  const DENTISTRY_BASE_CARDS: CardDef[] = [
    {
      id: "dentalProcedureType",
      cardType: "multi-choice",
      question: t(msg`What type of dental procedure are you seeking?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "dentalProcedureType",
      options: [
        { value: "implants", label: t(msg`Dental Implants`) },
        { value: "veneers", label: t(msg`Veneers`) },
        { value: "crowns", label: t(msg`Crowns`) },
        { value: "whitening", label: t(msg`Teeth Whitening`) },
        { value: "orthodontics", label: t(msg`Orthodontics/Braces`) },
        { value: "restoration", label: t(msg`General Restoration`) },
        { value: "cosmetic", label: t(msg`Cosmetic Dentistry`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "dentalDiscomfort",
      cardType: "text-input",
      question: t(msg`Do you experience any kind of dental discomfort?`),
      hint: t(msg`Describe any pain, sensitivity, or discomfort, or write 'None'.`),
      fieldKey: "dentalDiscomfort",
      placeholder: t(msg`Describe any pain...`),
    },
  ];

  const DENTISTRY_PHOTOS_CARD: CardDef = {
    id: "dental-photos",
    cardType: "dental-photos",
    question: t(msg`Upload your dental photos`),
    fieldKey: "photos",
  };

  // ─── Documents / uploads / links (single combined optional step) ───────────
  const DOCUMENTS_COMBINED_CARD: CardDef = {
    id: "documents-and-links",
    cardType: "documents-combined",
    question: t(msg`Documents & uploads`),
    hint: t(
      msg`Add lab or imaging files, optional photos, and links to cloud files — all in one place.`,
    ),
    isOptional: true,
    documentsCombinedConfig: {
      showLabsImaging: true,
      // Keep this screen consistent across all procedures, including dentistry.
      showAdditionalPhotos: true,
      showDocumentLinks: true,
    },
  };

  // ─── Procedure-specific card sets ──────────────────────────────────────────

  const VASCULAR_CARDS: CardDef[] = [
    {
      id: "vascularPrimaryGoal",
      cardType: "multi-choice",
      question: t(msg`What are your vascular concerns?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "vascularPrimaryGoal",
      options: [
        { value: "chest_pain", label: t(msg`Chest pain`) },
        { value: "circulation_issues", label: t(msg`Circulation issues (legs, arms)`) },
        { value: "varicose_veins", label: t(msg`Varicose veins`) },
        { value: "high_blood_pressure", label: t(msg`High blood pressure`) },
        { value: "stroke_prevention", label: t(msg`Stroke prevention`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "vascularSymptoms",
      cardType: "single-choice",
      question: t(msg`Are you currently experiencing active symptoms?`),
      fieldKey: "vascularSymptoms",
      autoAdvance: true,
      options: [
        { value: "yes_daily", label: t(msg`Yes, daily`) },
        { value: "yes_occasionally", label: t(msg`Yes, occasionally`) },
        { value: "no_symptoms", label: t(msg`No symptoms — preventative evaluation`) },
      ],
    },
    {
      id: "vascularSymptomTypes",
      cardType: "multi-choice",
      question: t(msg`Which symptoms are you experiencing?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "vascularSymptomTypes",
      options: [
        { value: "pain", label: t(msg`Pain`) },
        { value: "swelling", label: t(msg`Swelling`) },
        { value: "numbness", label: t(msg`Numbness`) },
        { value: "shortness_of_breath", label: t(msg`Shortness of breath`) },
        { value: "fatigue", label: t(msg`Fatigue`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "vascularDiagnosisStatus",
      cardType: "single-choice",
      question: t(msg`Have you been diagnosed with a vascular condition?`),
      fieldKey: "vascularDiagnosisStatus",
      autoAdvance: true,
      options: [
        { value: "known", label: t(msg`Yes — diagnosis known`) },
        { value: "unsure", label: t(msg`Yes — but unsure of details`) },
        { value: "seeking", label: t(msg`No — seeking evaluation`) },
      ],
    },
    {
      id: "vascularUrgency",
      cardType: "single-choice",
      question: t(msg`How soon are you looking to proceed?`),
      fieldKey: "vascularUrgency",
      autoAdvance: true,
      options: [
        { value: "immediately", label: t(msg`Immediately`) },
        { value: "1_2_months", label: t(msg`Within 1–2 months`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
    {
      id: "vascularProceduralExpectation",
      cardType: "single-choice",
      question: t(msg`What type of care are you expecting?`),
      fieldKey: "vascularProceduralExpectation",
      autoAdvance: true,
      options: [
        { value: "diagnostic", label: t(msg`Diagnostic testing only`) },
        { value: "non_surgical", label: t(msg`Non-surgical treatment`) },
        { value: "surgical", label: t(msg`Surgical intervention`) },
        { value: "consultation", label: t(msg`Doctor recommendation`) },
      ],
    },
  ];

  const HAIR_TRANSPLANT_CARDS: CardDef[] = [
    {
      id: "hairTransplantTreatmentAreas",
      cardType: "hair-area",
      question: t(msg`Which areas would you like treated?`),
      hint: t(
        msg`Tap zones on the diagram. It shows a general scalp map — skip beard if it does not apply to you.`,
      ),
      fieldKey: "hairTransplantTreatmentAreas",
    },
    {
      id: "hairTransplantProgression",
      cardType: "single-choice",
      question: t(msg`How is your hair loss changing over time?`),
      fieldKey: "hairTransplantProgression",
      autoAdvance: true,
      options: [
        { value: "stable", label: t(msg`Stable`) },
        { value: "slowly_worsening", label: t(msg`Slowly worsening`) },
        { value: "rapidly_worsening", label: t(msg`Rapidly worsening`) },
      ],
    },
    {
      id: "hairTransplantRelevantFactors",
      cardType: "multi-choice",
      question: t(msg`Do any of these apply to you?`),
      hint: t(
        msg`Helps us plan safe treatment (hormones, pregnancy, and more). Select all that apply, or “None of these apply”.`,
      ),
      fieldKey: "hairTransplantRelevantFactors",
      options: [
        {
          value: "pregnancy_breastfeeding",
          label: t(
            msg`Pregnancy or breastfeeding (now or in the past year)`,
          ),
        },
        {
          value: "postpartum",
          label: t(msg`Postpartum hair shedding (within the past 12 months)`),
        },
        {
          value: "menopause_hormonal",
          label: t(msg`Perimenopause or menopause-related hair changes`),
        },
        {
          value: "pcos_hormonal",
          label: t(msg`PCOS or other hormone-related hair loss`),
        },
        { value: "no_factors", label: t(msg`None of these apply`) },
      ],
    },
    {
      id: "hairTransplantPreviousTreatments",
      cardType: "multi-choice",
      question: t(msg`Have you tried any previous treatments?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "hairTransplantPreviousTreatments",
      options: [
        { value: "none", label: t(msg`None`) },
        {
          value: "medications",
          label: t(
            msg`Medications (e.g. minoxidil; spironolactone or finasteride only when prescribed)`,
          ),
        },
        { value: "prp", label: t(msg`PRP`) },
        { value: "previous_transplant", label: t(msg`Previous transplant`) },
      ],
    },
    {
      id: "hairTransplantOutcomeExpectation",
      cardType: "single-choice",
      question: t(msg`What outcome are you hoping for?`),
      fieldKey: "hairTransplantOutcomeExpectation",
      autoAdvance: true,
      options: [
        { value: "natural_hairline", label: t(msg`Natural-looking hairline`) },
        {
          value: "part_mid_scalp",
          label: t(msg`Better density at the part / mid-scalp`),
        },
        { value: "density", label: t(msg`Overall density`) },
        { value: "coverage", label: t(msg`Coverage`) },
        { value: "minimal_scarring", label: t(msg`Minimal scarring`) },
      ],
    },
    {
      id: "hairTransplantTimeline",
      cardType: "single-choice",
      question: t(msg`What's your preferred timeline?`),
      fieldKey: "hairTransplantTimeline",
      autoAdvance: true,
      options: [
        { value: "asap", label: t(msg`As soon as possible`) },
        { value: "1_3_months", label: t(msg`Within 1–3 months`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  const OPTICAL_CARDS: CardDef[] = [
    {
      id: "opticalPrimaryReason",
      cardType: "multi-choice",
      question: t(msg`What brings you to an optical specialist?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "opticalPrimaryReason",
      options: [
        { value: "blurry_vision", label: t(msg`Blurry vision`) },
        { value: "glasses_contacts", label: t(msg`Glasses or contacts`) },
        { value: "lasik_evaluation", label: t(msg`LASIK evaluation`) },
        { value: "cataracts", label: t(msg`Cataracts`) },
        { value: "disease_monitoring", label: t(msg`Eye disease monitoring`) },
        { value: "routine_exam", label: t(msg`Routine exam`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "opticalVisionCorrection",
      cardType: "single-choice",
      question: t(msg`Do you currently use vision correction?`),
      fieldKey: "opticalVisionCorrection",
      autoAdvance: true,
      options: [
        { value: "glasses", label: t(msg`Glasses`) },
        { value: "contacts", label: t(msg`Contact lenses`) },
        { value: "both", label: t(msg`Both`) },
        { value: "neither", label: t(msg`Neither`) },
      ],
    },
    {
      id: "opticalLaterality",
      cardType: "single-choice",
      question: t(msg`Which eye is affected?`),
      fieldKey: "opticalLaterality",
      autoAdvance: true,
      options: [
        { value: "left", label: t(msg`Left`) },
        { value: "right", label: t(msg`Right`) },
        { value: "both", label: t(msg`Both`) },
      ],
    },
    {
      id: "opticalSymptoms",
      cardType: "multi-choice",
      question: t(msg`Are you experiencing any of these symptoms?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "opticalSymptoms",
      options: [
        { value: "pain", label: t(msg`Eye pain`) },
        { value: "headaches", label: t(msg`Headaches`) },
        { value: "dry_eyes", label: t(msg`Dry eyes`) },
        { value: "light_sensitivity", label: t(msg`Light sensitivity`) },
        { value: "night_vision", label: t(msg`Night vision issues`) },
        { value: "none", label: t(msg`None`) },
      ],
    },
    {
      id: "opticalProcedureInterest",
      cardType: "single-choice",
      question: t(msg`Are you interested in a corrective procedure?`),
      fieldKey: "opticalProcedureInterest",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
        { value: "unsure", label: t(msg`Unsure — want evaluation`) },
      ],
    },
  ];

  const PLASTIC_SURGERY_CARDS: CardDef[] = [
    {
      id: "plasticAreaOfInterest",
      cardType: "multi-choice",
      question: t(msg`Which area are you interested in?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "plasticAreaOfInterest",
      options: [
        { value: "face", label: t(msg`Face`) },
        { value: "breast", label: t(msg`Breast`) },
        { value: "body", label: t(msg`Body`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "plasticProcedureType",
      cardType: "single-choice",
      question: t(msg`What type of procedure are you considering?`),
      fieldKey: "plasticProcedureType",
      autoAdvance: true,
      options: [
        { value: "cosmetic", label: t(msg`Cosmetic enhancement`) },
        { value: "reconstructive", label: t(msg`Reconstructive surgery`) },
        { value: "revision", label: t(msg`Revision of prior surgery`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "plasticSpecificConcern",
      cardType: "single-choice",
      question: t(msg`What is your primary goal?`),
      fieldKey: "plasticSpecificConcern",
      autoAdvance: true,
      options: [
        { value: "appearance", label: t(msg`Appearance improvement`) },
        { value: "symmetry", label: t(msg`Symmetry`) },
        { value: "function", label: t(msg`Function`) },
        { value: "correction", label: t(msg`Correction of prior work`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "plasticPreviousSurgery",
      cardType: "single-choice",
      question: t(msg`Have you had previous plastic surgery?`),
      fieldKey: "plasticPreviousSurgery",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
      ],
    },
    {
      id: "plasticOutcomePriority",
      cardType: "single-choice",
      question: t(msg`What is your top priority for results?`),
      fieldKey: "plasticOutcomePriority",
      autoAdvance: true,
      options: [
        { value: "natural", label: t(msg`Natural look`) },
        { value: "minimal_downtime", label: t(msg`Minimal downtime`) },
        { value: "longevity", label: t(msg`Longevity of results`) },
        { value: "cost", label: t(msg`Cost efficiency`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
  ];

  const DIAGNOSTICS_CARDS: CardDef[] = [
    {
      id: "diagnosticsPurpose",
      cardType: "multi-choice",
      question: t(msg`What is the purpose of your diagnostic test?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "diagnosticsPurpose",
      options: [
        { value: "doctor_recommendation", label: t(msg`Doctor recommendation`) },
        { value: "ongoing_condition", label: t(msg`Ongoing condition`) },
        { value: "new_symptoms", label: t(msg`New symptoms`) },
        { value: "preventative", label: t(msg`Preventative screening`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "diagnosticsType",
      cardType: "multi-choice",
      question: t(msg`What type of imaging or test are you seeking?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "diagnosticsType",
      options: [
        { value: "mri", label: "MRI" },
        { value: "ct", label: "CT" },
        { value: "xray", label: "X-ray" },
        { value: "ultrasound", label: t(msg`Ultrasound`) },
        { value: "pet", label: "PET" },
        { value: "unsure", label: t(msg`Not sure`) },
      ],
    },
    {
      id: "diagnosticsArea",
      cardType: "multi-choice",
      question: t(msg`Which area of the body needs evaluation?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "diagnosticsArea",
      options: [
        { value: "head_brain", label: t(msg`Head / Brain`) },
        { value: "spine", label: t(msg`Spine`) },
        { value: "chest", label: t(msg`Chest`) },
        { value: "abdomen", label: t(msg`Abdomen`) },
        { value: "pelvis", label: t(msg`Pelvis`) },
        { value: "extremities", label: t(msg`Extremities`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "diagnosticsTimeSensitivity",
      cardType: "single-choice",
      question: t(msg`How time-sensitive is this?`),
      fieldKey: "diagnosticsTimeSensitivity",
      autoAdvance: true,
      options: [
        { value: "urgent", label: t(msg`Urgent`) },
        { value: "within_weeks", label: t(msg`Within weeks`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  const FERTILITY_CARDS: CardDef[] = [
    {
      id: "fertilityCareFocus",
      cardType: "multi-choice",
      question: t(msg`What fertility care are you looking for?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "fertilityCareFocus",
      options: [
        { value: "evaluation", label: t(msg`Fertility evaluation`) },
        { value: "ivf", label: "IVF" },
        { value: "egg_freezing", label: t(msg`Egg freezing`) },
        { value: "sperm_analysis", label: t(msg`Sperm analysis`) },
        { value: "hormonal", label: t(msg`Hormonal assessment`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "fertilityPatientType",
      cardType: "single-choice",
      question: t(msg`Are you seeking care as…`),
      fieldKey: "fertilityPatientType",
      autoAdvance: true,
      options: [
        { value: "individual", label: t(msg`An individual`) },
        { value: "couple", label: t(msg`A couple`) },
      ],
    },
    {
      id: "fertilityPreviousAttempts",
      cardType: "single-choice",
      question: t(msg`Have you already attempted fertility treatments?`),
      fieldKey: "fertilityPreviousAttempts",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
      ],
    },
    {
      id: "fertilityKnownDiagnosis",
      cardType: "single-choice",
      question: t(msg`Do you have a known fertility diagnosis?`),
      fieldKey: "fertilityKnownDiagnosis",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
        { value: "unsure", label: t(msg`Unsure`) },
      ],
    },
    {
      id: "fertilityTimeline",
      cardType: "single-choice",
      question: t(msg`What is your timeline?`),
      fieldKey: "fertilityTimeline",
      autoAdvance: true,
      options: [
        { value: "immediately", label: t(msg`Immediately`) },
        { value: "months_3_6", label: t(msg`Within 3–6 months`) },
        { value: "planning", label: t(msg`Planning stage`) },
      ],
    },
  ];

  const STEM_CELL_CARDS: CardDef[] = [
    {
      id: "stemCellReason",
      cardType: "multi-choice",
      question: t(msg`What is your primary reason for stem cell therapy?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "stemCellReason",
      options: [
        { value: "joint_pain", label: t(msg`Joint pain`) },
        { value: "arthritis", label: t(msg`Arthritis`) },
        { value: "sports_injury", label: t(msg`Sports injury`) },
        { value: "neurological", label: t(msg`Neurological condition`) },
        { value: "autoimmune", label: t(msg`Autoimmune condition`) },
        { value: "anti_aging", label: t(msg`Anti-aging / wellness`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "stemCellLocation",
      cardType: "multi-choice",
      question: t(msg`Which area needs to be treated?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "stemCellLocation",
      options: [
        { value: "knee", label: t(msg`Knee`) },
        { value: "shoulder", label: t(msg`Shoulder`) },
        { value: "hip", label: t(msg`Hip`) },
        { value: "spine", label: t(msg`Spine`) },
        { value: "whole_body", label: t(msg`Whole-body / systemic`) },
      ],
    },
    {
      id: "stemCellDiagnosisStatus",
      cardType: "single-choice",
      question: t(msg`Do you have a formal diagnosis?`),
      fieldKey: "stemCellDiagnosisStatus",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
        { value: "unsure", label: t(msg`Unsure`) },
      ],
    },
    {
      id: "stemCellTreatmentGoal",
      cardType: "single-choice",
      question: t(msg`What is your treatment goal?`),
      fieldKey: "stemCellTreatmentGoal",
      autoAdvance: true,
      options: [
        { value: "pain_reduction", label: t(msg`Pain reduction`) },
        { value: "mobility", label: t(msg`Improved mobility`) },
        { value: "degeneration", label: t(msg`Slowed degeneration`) },
        { value: "recovery", label: t(msg`Recovery after injury`) },
        { value: "wellness", label: t(msg`General wellness`) },
      ],
    },
    {
      id: "stemCellPriorTreatments",
      cardType: "multi-choice",
      question: t(msg`Have you tried any prior treatments?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "stemCellPriorTreatments",
      options: [
        { value: "physical_therapy", label: t(msg`Physical therapy`) },
        { value: "injections", label: t(msg`Injections`) },
        { value: "surgery", label: t(msg`Surgery`) },
        { value: "none", label: t(msg`None`) },
      ],
    },
  ];

  const BARIATRIC_CARDS: CardDef[] = [
    {
      id: "bariatricPrimaryGoal",
      cardType: "multi-choice",
      question: t(msg`What is your primary goal with bariatric surgery?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "bariatricPrimaryGoal",
      options: [
        { value: "weight_loss", label: t(msg`Weight loss`) },
        { value: "improve_conditions", label: t(msg`Improve obesity-related conditions`) },
        { value: "improve_mobility", label: t(msg`Improve mobility`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "bariatricPriorWeightLoss",
      cardType: "multi-choice",
      question: t(msg`What weight loss methods have you tried before?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "bariatricPriorWeightLoss",
      options: [
        { value: "diet_exercise", label: t(msg`Diet & Exercise`) },
        { value: "medications", label: t(msg`Weight loss medications`) },
        { value: "previous_surgery", label: t(msg`Previous bariatric surgery`) },
        { value: "none", label: t(msg`None`) },
      ],
    },
    {
      id: "bariatricProcedureInterest",
      cardType: "single-choice",
      question: t(msg`Which procedure are you most interested in?`),
      fieldKey: "bariatricProcedureInterest",
      autoAdvance: true,
      options: [
        { value: "gastric_sleeve", label: t(msg`Gastric Sleeve`) },
        { value: "gastric_bypass", label: t(msg`Gastric Bypass`) },
        { value: "gastric_balloon", label: t(msg`Gastric Balloon`) },
        { value: "revision", label: t(msg`Revision Surgery`) },
        { value: "undecided", label: t(msg`Undecided`) },
      ],
    },
    {
      id: "bariatricCurrentDiet",
      cardType: "single-choice",
      question: t(msg`How would you describe your current diet?`),
      fieldKey: "bariatricCurrentDiet",
      autoAdvance: true,
      options: [
        { value: "healthy", label: t(msg`Generally healthy`) },
        { value: "emotional", label: t(msg`Emotional eating`) },
        { value: "snacking", label: t(msg`Frequent snacking`) },
        { value: "large_portions", label: t(msg`Large portions`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "bariatricTimeline",
      cardType: "single-choice",
      question: t(msg`What is your preferred timeline?`),
      fieldKey: "bariatricTimeline",
      autoAdvance: true,
      options: [
        { value: "asap", label: t(msg`As soon as possible`) },
        { value: "months_3_6", label: t(msg`Within 3–6 months`) },
        { value: "months_6_12", label: t(msg`Within 6–12 months`) },
        { value: "planning", label: t(msg`Planning stage`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  const WELLNESS_GENERAL_CARDS: CardDef[] = [
    {
      id: "wellnessPrimaryGoal",
      cardType: "multi-choice",
      question: t(msg`What is your primary wellness goal?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "wellnessPrimaryGoal",
      options: [
        { value: "preventive_care", label: t(msg`Preventive Care`) },
        { value: "holistic_healing", label: t(msg`Holistic Healing`) },
        { value: "anti_aging", label: t(msg`Anti-aging / Longevity`) },
        { value: "health_optimization", label: t(msg`Health Optimization`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "wellnessCurrentPractices",
      cardType: "multi-choice",
      question: t(msg`What wellness practices do you currently follow?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "wellnessCurrentPractices",
      options: [
        { value: "nutritional_supplements", label: t(msg`Nutritional Supplements`) },
        { value: "meditation_mindfulness", label: t(msg`Meditation / Mindfulness`) },
        { value: "regular_exercise", label: t(msg`Regular Exercise`) },
        { value: "alternative_medicine", label: t(msg`Alternative Medicine`) },
        { value: "none", label: t(msg`None`) },
      ],
    },
    {
      id: "wellnessSpecificConcerns",
      cardType: "multi-choice",
      question: t(msg`What specific concern brings you here?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "wellnessSpecificConcerns",
      options: [
        { value: "sleep_issues", label: t(msg`Sleep Issues`) },
        { value: "stress_anxiety", label: t(msg`Stress / Anxiety`) },
        { value: "energy_levels", label: t(msg`Energy Levels`) },
        { value: "nutrition", label: t(msg`Nutrition`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "wellnessTimeline",
      cardType: "single-choice",
      question: t(msg`What's your timeline?`),
      fieldKey: "wellnessTimeline",
      autoAdvance: true,
      options: [
        { value: "asap", label: t(msg`As soon as possible`) },
        { value: "months_3_6", label: t(msg`Within 3–6 months`) },
        { value: "planning", label: t(msg`Planning stage`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  const WELLNESS_THERAPY_CARDS: CardDef[] = [
    {
      id: "wellnessPrimaryGoal",
      cardType: "multi-choice",
      question: t(msg`What is your main goal for this therapy?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "wellnessPrimaryGoal",
      options: [
        { value: "pain_relief", label: t(msg`Pain relief`) },
        { value: "improved_mobility", label: t(msg`Improved mobility`) },
        { value: "general_wellness", label: t(msg`General wellness`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "wellnessPreviousExperience",
      cardType: "single-choice",
      question: t(msg`Have you tried this type of therapy before?`),
      fieldKey: "wellnessPreviousExperience",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
        { value: "unsure", label: t(msg`Unsure`) },
      ],
    },
    {
      id: "wellnessTimeline",
      cardType: "single-choice",
      question: t(msg`What's your preferred timeline to start?`),
      fieldKey: "wellnessTimeline",
      autoAdvance: true,
      options: [
        { value: "asap", label: t(msg`As soon as possible`) },
        { value: "months_3_6", label: t(msg`Within 3–6 months`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  const OTHER_SPECIALTY_CARDS: CardDef[] = [
    {
      id: "otherSpecialtyFocus",
      cardType: "multi-choice",
      question: t(msg`What best describes your medical need?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "otherSpecialtyFocus",
      options: [
        { value: "consultation", label: t(msg`General specialist consultation`) },
        { value: "second_opinion", label: t(msg`Second medical opinion`) },
        { value: "symptom_evaluation", label: t(msg`Evaluation of symptoms`) },
        { value: "treatment_planning", label: t(msg`Treatment planning`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "otherSpecialtyBodyAreas",
      cardType: "multi-choice",
      question: t(msg`Which body area(s) are involved?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "otherSpecialtyBodyAreas",
      options: [
        { value: "head_neck", label: t(msg`Head / neck`) },
        { value: "chest", label: t(msg`Chest`) },
        { value: "abdomen", label: t(msg`Abdomen`) },
        { value: "back_spine", label: t(msg`Back / spine`) },
        { value: "arms_legs", label: t(msg`Arms / legs`) },
        { value: "multiple", label: t(msg`Multiple areas`) },
        { value: "none", label: t(msg`Not area-specific`) },
      ],
    },
    {
      id: "otherSpecialtySymptoms",
      cardType: "multi-choice",
      question: t(msg`Which of these apply to your condition?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "otherSpecialtySymptoms",
      options: [
        { value: "pain", label: t(msg`Pain`) },
        { value: "swelling", label: t(msg`Swelling`) },
        { value: "mobility_limitation", label: t(msg`Mobility limitation`) },
        { value: "fatigue", label: t(msg`Fatigue`) },
        { value: "recent_diagnosis", label: t(msg`Recent diagnosis`) },
        { value: "none", label: t(msg`No active symptoms`) },
      ],
    },
    {
      id: "otherSpecialtyTimeline",
      cardType: "single-choice",
      question: t(msg`When would you like to be seen?`),
      fieldKey: "otherSpecialtyTimeline",
      autoAdvance: true,
      options: [
        { value: "asap", label: t(msg`As soon as possible`) },
        { value: "1_2_weeks", label: t(msg`Within 1-2 weeks`) },
        { value: "1_2_months", label: t(msg`Within 1-2 months`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
    {
      id: "otherSpecialtyAdditionalInfo",
      cardType: "text-input",
      question: t(msg`Anything else our team should know?`),
      hint: t(msg`Share key context, tests done, or goals for this consultation.`),
      fieldKey: "otherSpecialtyAdditionalInfo",
      placeholder: t(msg`Type your notes here...`),
    },
  ];

  const ORTHOPEDIC_CARDS: CardDef[] = [
    {
      id: "orthopedicPrimaryConcern",
      cardType: "multi-choice",
      question: t(msg`What is your primary orthopedic concern?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "orthopedicPrimaryConcern",
      options: [
        { value: "joint_pain", label: t(msg`Joint pain`) },
        { value: "sports_injury", label: t(msg`Sports injury`) },
        { value: "spine_back", label: t(msg`Spine/Back issues`) },
        { value: "arthritis", label: t(msg`Arthritis`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "orthopedicAffectedArea",
      cardType: "multi-choice",
      question: t(msg`Which area of the body is affected?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "orthopedicAffectedArea",
      options: [
        { value: "knee", label: t(msg`Knee`) },
        { value: "hip", label: t(msg`Hip`) },
        { value: "shoulder", label: t(msg`Shoulder`) },
        { value: "spine", label: t(msg`Spine/Back`) },
        { value: "hand_wrist", label: t(msg`Hand/Wrist`) },
        { value: "foot_ankle", label: t(msg`Foot/Ankle`) },
      ],
    },
    {
      id: "orthopedicDuration",
      cardType: "single-choice",
      question: t(msg`How long have you been experiencing this?`),
      fieldKey: "orthopedicDuration",
      autoAdvance: true,
      options: [
        { value: "recent", label: t(msg`Recent injury (days/weeks)`) },
        { value: "1_3_months", label: t(msg`1-3 months`) },
        { value: "3_6_months", label: t(msg`3-6 months`) },
        { value: "over_year", label: t(msg`Over a year`) },
      ],
    },
    {
      id: "orthopedicPriorTreatments",
      cardType: "multi-choice",
      question: t(msg`What treatments have you tried?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "orthopedicPriorTreatments",
      options: [
        { value: "physical_therapy", label: t(msg`Physical therapy`) },
        { value: "injections", label: t(msg`Injections`) },
        { value: "medications", label: t(msg`Medications`) },
        { value: "prior_surgery", label: t(msg`Prior surgery`) },
        { value: "none", label: t(msg`None`) },
      ],
    },
  ];

  const ENT_CARDS: CardDef[] = [
    {
      id: "entPrimaryConcern",
      cardType: "multi-choice",
      question: t(msg`What is your primary ENT concern?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "entPrimaryConcern",
      options: [
        { value: "hearing_loss", label: t(msg`Hearing loss`) },
        { value: "sinus_issues", label: t(msg`Sinus issues`) },
        { value: "throat_vocal", label: t(msg`Throat/Vocal`) },
        { value: "ear_infection_pain", label: t(msg`Ear infection/pain`) },
        { value: "sleep_apnea", label: t(msg`Sleep apnea`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "entSymptoms",
      cardType: "multi-choice",
      question: t(msg`What symptoms are you experiencing?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "entSymptoms",
      options: [
        { value: "nasal_congestion", label: t(msg`Nasal congestion`) },
        { value: "tinnitus", label: t(msg`Ringing in ears (tinnitus)`) },
        { value: "vertigo_dizziness", label: t(msg`Vertigo/Dizziness`) },
        { value: "swallowing_difficulty", label: t(msg`Difficulty swallowing`) },
        { value: "snoring", label: t(msg`Snoring`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "entDuration",
      cardType: "single-choice",
      question: t(msg`How long have you been experiencing this?`),
      fieldKey: "entDuration",
      autoAdvance: true,
      options: [
        { value: "less_month", label: t(msg`Less than a month`) },
        { value: "1_6_months", label: t(msg`1-6 months`) },
        { value: "over_6_months", label: t(msg`Over 6 months`) },
      ],
    },
    {
      id: "entPreviousSurgeries",
      cardType: "single-choice",
      question: t(msg`Have you had any prior ENT surgeries?`),
      fieldKey: "entPreviousSurgeries",
      autoAdvance: true,
      options: [
        { value: "yes", label: t(msg`Yes`) },
        { value: "no", label: t(msg`No`) },
      ],
    },
  ];

  const NEURO_SURGERY_CARDS: CardDef[] = [
    {
      id: "neuroSurgeryPrimaryConcern",
      cardType: "multi-choice",
      question: t(msg`What is your primary neurosurgery concern?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "neuroSurgeryPrimaryConcern",
      options: [
        { value: "brain_tumor", label: t(msg`Brain tumor`) },
        { value: "spine_disc", label: t(msg`Spine disc / nerve compression`) },
        { value: "aneurysm_avm", label: t(msg`Aneurysm / AVM`) },
        { value: "brain_injury", label: t(msg`Brain or spinal injury`) },
        { value: "movement_disorder", label: t(msg`Movement disorder`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "neuroSurgerySymptoms",
      cardType: "multi-choice",
      question: t(msg`Which symptoms are you experiencing?`),
      hint: t(msg`Select all that apply.`),
      fieldKey: "neuroSurgerySymptoms",
      options: [
        { value: "headache", label: t(msg`Severe headaches`) },
        { value: "weakness_numbness", label: t(msg`Weakness or numbness`) },
        { value: "balance_issues", label: t(msg`Balance or walking issues`) },
        { value: "seizures", label: t(msg`Seizures`) },
        { value: "pain", label: t(msg`Persistent neck/back pain`) },
        { value: "other", label: t(msg`Other`) },
      ],
    },
    {
      id: "neuroSurgeryDiagnosisStatus",
      cardType: "single-choice",
      question: t(msg`Do you already have imaging or a diagnosis?`),
      fieldKey: "neuroSurgeryDiagnosisStatus",
      autoAdvance: true,
      options: [
        { value: "confirmed", label: t(msg`Yes, confirmed diagnosis`) },
        { value: "imaging_only", label: t(msg`Imaging done, awaiting diagnosis`) },
        { value: "evaluation_needed", label: t(msg`No, I need evaluation`) },
      ],
    },
    {
      id: "neuroSurgeryPriorTreatment",
      cardType: "single-choice",
      question: t(msg`Have you had prior treatment for this condition?`),
      fieldKey: "neuroSurgeryPriorTreatment",
      autoAdvance: true,
      options: [
        { value: "none", label: t(msg`No prior treatment`) },
        { value: "medications", label: t(msg`Medication management`) },
        { value: "procedure_surgery", label: t(msg`Prior procedure or surgery`) },
        { value: "rehab_therapy", label: t(msg`Rehabilitation / therapy`) },
      ],
    },
    {
      id: "neuroSurgeryTimeline",
      cardType: "single-choice",
      question: t(msg`How soon would you like to be seen?`),
      fieldKey: "neuroSurgeryTimeline",
      autoAdvance: true,
      options: [
        { value: "urgent", label: t(msg`Urgent (as soon as possible)`) },
        { value: "weeks_2_4", label: t(msg`Within 2-4 weeks`) },
        { value: "months_1_2", label: t(msg`Within 1-2 months`) },
        { value: "flexible", label: t(msg`Flexible`) },
      ],
    },
  ];

  // ─── End cards ─────────────────────────────────────────────────────────────
  const REVIEW_CARD: CardDef = {
    id: "review",
    cardType: "review",
    question: t(msg`Review your information`),
    hint: t(msg`Please review everything before submitting.`),
  };

  const OTHER_SPECIALTY_REVIEW_CARD: CardDef = {
    id: "other-specialty-review",
    cardType: "other-specialty-review",
    question: t(msg`Review your inquiry`),
    hint: t(
      msg`We’ll email these details to our team—no document upload is required for this path.`,
    ),
  };

  const OTHER_SPECIALTY_THANK_YOU_CARD: CardDef = {
    id: "other-specialty-thank-you",
    cardType: "other-specialty-thank-you",
    question: t(msg`Thank you`),
    hint: t(msg`Your inquiry has been sent.`),
  };

  const VERIFICATION_CARD: CardDef = {
    id: "verification",
    cardType: "verification",
    question: t(msg`Verify your email`),
    hint: t(msg`We've sent a verification code to your email.`),
  };

  // ─── Assemble flow ─────────────────────────────────────────────────────────
  let procedureCards: CardDef[] = [];

  if (slug === "dentistry") {
    procedureCards.push(...DENTISTRY_BASE_CARDS);
    if (dentalDynamicCards.length > 0) {
      procedureCards.push(...dentalDynamicCards);
    }
    procedureCards.push(DENTISTRY_PHOTOS_CARD);
  } else if (slug === "vascular") {
    procedureCards = VASCULAR_CARDS;
  } else if (slug.includes("hair") || slug === "hair-transplant") {
    procedureCards = HAIR_TRANSPLANT_CARDS;
  } else if (slug.includes("optical") || slug === "optics") {
    procedureCards = OPTICAL_CARDS;
  } else if (slug.includes("plastic") || slug === "cosmetic") {
    procedureCards = PLASTIC_SURGERY_CARDS;
  } else if (
    slug.includes("diagnostic") ||
    slug.includes("imaging") ||
    slug === "radiology"
  ) {
    procedureCards = DIAGNOSTICS_CARDS;
  } else if (slug === "orthopedic" || slug === "orthopedics") {
    procedureCards = ORTHOPEDIC_CARDS;
  } else if (
    slug === "ent" ||
    slug === "ear-nose-throat" ||
    slug === "ear-nose-and-throat"
  ) {
    procedureCards = ENT_CARDS;
  } else if (slug === "neuro-surgery") {
    procedureCards = NEURO_SURGERY_CARDS;
  } else if (slug === "fertility") {
    procedureCards = FERTILITY_CARDS;
  } else if (slug.includes("stem")) {
    procedureCards = STEM_CELL_CARDS;
  } else if (slug === "bariatric" || slug.includes("bariatric")) {
    procedureCards = BARIATRIC_CARDS;
  } else if (
    slug === "wellness_healthcare" ||
    slug === "wellness-healthcare" ||
    slug === "wellness"
  ) {
    procedureCards = WELLNESS_GENERAL_CARDS;
  } else if (
    slug.includes("acupuncture") ||
    slug.includes("colema") ||
    slug.includes("hyperbaric") ||
    slug.includes("massage") ||
    slug.includes("reflexology") ||
    slug.includes("ozone") ||
    slug.includes("functional") ||
    slug.includes("quantum") ||
    slug.includes("iv") ||
    slug.includes("yoga") ||
    slug.includes("chinese")
  ) {
    procedureCards = WELLNESS_THERAPY_CARDS;
  } else if (slug === "other") {
    procedureCards = OTHER_SPECIALTY_CARDS;
  }

  const isOtherSpecialty = slug === "other";

  const endCards: CardDef[] = isOtherSpecialty
    ? [OTHER_SPECIALTY_REVIEW_CARD, OTHER_SPECIALTY_THANK_YOU_CARD]
    : [REVIEW_CARD, VERIFICATION_CARD];

  return [
    ...PERSONAL_CARDS,
    PROCEDURE_CARD,
    ...procedureCards,
    ...(isOtherSpecialty ? [] : [DOCUMENTS_COMBINED_CARD]),
    ...endCards,
  ];
}
